import React, { useContext, useState } from 'react'
import styled from 'styled-components'
import i18n from 'live/services/i18n'
import Auth from 'live/services/authorization'
import { StoppedLineModal } from './stoppedLineModal'
import { getAssetLink } from '../../common/utils'
import { ParamsContext } from '../qualityMonitoring'
import { motion, AnimatePresence } from 'framer-motion'

const buttonVariants = {
    hidden: { opacity: 0, scale: 0 },
    visible: { opacity: 1, scale: 1 }
}

const containerVariants = {
    hidden: {
        height: 0,
        transition: { when: 'afterChildren', staggerChildren: 0.2 }
    },
    visible: {
        height: 'auto',
        transition: {
            staggerChildren: 0.2
        }
    }
}

export function AnalysisButtons(): JSX.Element {
    const urlParams = useContext(ParamsContext)
    const analysisTypes = ['icp', 'icm']
    const userPermissions = Auth.Permission
    const paramsArray = urlParams.split('/')
    const disabledConditional = paramsArray.length < 4
    const [showModal, setShowModal] = useState(false)

    return (
        <AnimatePresence>
            {disabledConditional ? null : (
                <StyledAnalysisButtons
                    className="analysisButtons"
                    variants={containerVariants}
                    initial={'hidden'}
                    animate={'visible'}
                    exit={'hidden'}
                    key="container"
                >
                    {userPermissions.QUALITY_OPERATOR ||
                    userPermissions.QUALITY_ANALYST ||
                    userPermissions.QUALITY_ADMIN ||
                    userPermissions.isAdmin ? (
                        <a href={paramsArray.length === 5 ? getAssetLink(`${urlParams}/icc`) : null}>
                            <motion.button
                                variants={buttonVariants}
                                className={`analysisButtons__button icc`}
                                key="icc"
                            >
                                <div className={`analysisButtons__button--text icc`}>
                                    <i className="far fa-plus" /> {i18n('icc').toUpperCase()}
                                </div>
                                {paramsArray.length === 4 ? (
                                    <ul className={`analysisButtons__button--icc__optionList`}>
                                        <li className={`analysisButtons__button--icc__optionList--option`}>
                                            <a
                                                href={getAssetLink(`${urlParams}/icc`)}
                                                className={`analysisButtons__button--icc__optionList--option__newAnalysis`}
                                            >
                                                {i18n('New Analysis')}
                                            </a>
                                        </li>

                                        <li className={`analysisButtons__button--icc__optionList--option`}>
                                            <button
                                                onClick={() => setShowModal(true)}
                                                className={`analysisButtons__button--icc__optionList--option__stoppedLine`}
                                            >
                                                {i18n('Stopped Line')}
                                            </button>
                                        </li>
                                    </ul>
                                ) : null}
                            </motion.button>
                        </a>
                    ) : null}
                    {userPermissions.QUALITY_ANALYST || userPermissions.QUALITY_ADMIN || userPermissions.isAdmin
                        ? analysisTypes.map(analysis => (
                              <motion.a
                                  variants={buttonVariants}
                                  key={analysis}
                                  className={`analysisButtons__button ${analysis}`}
                                  href={disabledConditional ? null : getAssetLink(`${urlParams}/${analysis}`)}
                              >
                                  <div className={`analysisButtons__button--link__text ${analysis}`}>
                                      <i className="far fa-plus" /> {i18n(analysis).toUpperCase()}
                                  </div>
                              </motion.a>
                          ))
                        : null}
                    <StoppedLineModal showModal={showModal} setShowModal={setShowModal} />
                </StyledAnalysisButtons>
            )}
        </AnimatePresence>
    )
}

const StyledAnalysisButtons = styled(motion.div)`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    .analysisButtons {
        &__button {
            margin-right: 32px;
            background: transparent;
            font-size: 28px;
            color: ${({ theme }) => theme['blue-500']};
            border: 2px solid ${({ theme }) => theme['blue-500']};
            border-radius: 6px;
            position: relative;
            transition: all 0.3s ease-in-out;
            &--link__text,
            &--text {
                padding: 17px 85px;
            }
            &:hover,
            &:focus {
                text-decoration: none;
                background: ${({ theme }) => theme['blue-700']};
                color: ${({ theme }) => theme['gray-0']};
            }
            &:focus-within,
            &:focus {
                ul.analysisButtons__button--icc__optionList {
                    display: flex;
                }
            }
            &--icc {
                &__optionList {
                    display: none;
                    position: absolute;
                    bottom: -5px;
                    left: 0;
                    background: ${({ theme }) => theme['gray-0']};
                    border-radius: 3px;
                    box-shadow: 0px 0px 4px rgba(54, 65, 89, 0.25);
                    font-size: 18px;
                    width: 100%;
                    padding: 16px 0;
                    flex-direction: column;
                    align-items: flex-start;
                    margin: 0;
                    transform: translateY(100%);
                    &--option {
                        margin-bottom: 3px;
                        width: 100%;
                        &__newAnalysis,
                        &__stoppedLine {
                            background: none;
                            border: none;
                            width: 100%;
                            padding: 8px 16px;
                            display: flex;
                            color: ${({ theme }) => theme['blue-500']};

                            &:hover,
                            &:focus {
                                background: ${({ theme }) => theme['blue-200']};
                                text-decoration: none;
                            }
                        }
                    }
                }
            }
        }
    }
`
